import { Box, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import ConfigChange, { ConfigChangeProps } from './ConfigChange';
import { useDispatch } from 'react-redux';
import { getBasicUserDetail } from '../../actions/users';
import { Pagination } from '@codametrix/ui-components';
import { CMxComponents } from '@codametrix/ui-components/src/types';

export interface ConfigChangeListProps {
  changes: ConfigChangeProps['change'][];
  auditData: {
    content: ConfigChangeProps['change'][];
    pageable: {
      pageNumber: number;
      pageSize: number;
      offset: number;
      sort: {
        sorted: boolean;
        unsorted: boolean;
        empty: boolean;
      };
      paged: boolean;
      unpaged: boolean;
    };
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    first: boolean;
    numberOfElements: number;
    empty: boolean;
  };
  onPageChange: (pageNumber: number) => void;
}

const userCache: Record<
  string,
  {
    username: string;
    firstName: string;
    lastName: string;
  }
> = {};

const ConfigChangeList: React.FC<ConfigChangeListProps> = ({
  changes,
  auditData,
  onPageChange
}) => {
  const dispatch = useDispatch();
  const [userDetailsMap, setUserDetailsMap] = useState<typeof userCache>({});

  useEffect(() => {
    const fetchMissingUserDetails = async () => {
      const uniqueIds = Array.from(
        new Set(changes.map(c => c.userName).filter(Boolean))
      ) as string[];

      const uncachedIds = uniqueIds.filter(id => !userCache[id]);

      const promises = uncachedIds.map(async userId => {
        try {
          const result = (await (dispatch as any)(
            getBasicUserDetail({ userId })
          )) as {
            username: string;
            firstName: string;
            lastName: string;
          };
          userCache[userId] = result;
        } catch (e) {
          console.error(`Failed to fetch user ${userId}`, e);
          userCache[userId] = { username: userId, firstName: '', lastName: '' }; // fallback
        }
      });

      await Promise.all(promises);
      setUserDetailsMap({ ...userCache });
    };

    fetchMissingUserDetails();
  }, [changes, dispatch]);

  const handlePageChange = (action: CMxComponents.PaginationAction) => {
    const newPage = action.index + 1;
    onPageChange(newPage);
  };

  return (
    <VStack spacing={4} align="stretch" height="100%">
      {/* Changes list */}
      <Box flex="1" overflow="auto">
        <VStack spacing={4} align="stretch">
          {changes.map((change, index) => (
            <ConfigChange
              key={index}
              change={change}
              userDetails={userDetailsMap[change.userName ?? '']}
            />
          ))}
        </VStack>
      </Box>

      {/* Pagination at bottom */}
      <Box mt="auto" pt={4}>
        <Pagination pageable={auditData} onSelect={handlePageChange} />
      </Box>
    </VStack>
  );
};

export default ConfigChangeList;
